import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import { ItemNoAndType } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { Ref, useImperativeHandle } from "react";
import RefillQuantity from "../../../RefillQuantity/RefillQuantity";
import { useFilteredSalesLocations } from "@coworker/apprestructured/src/task-management/services/useSalesLocations";
import { Location } from "@coworker/apprestructured/src/task-management/types/SalesLocation";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { useRefillQuantity } from "../../../../services/useRefillQuantity";

export type RefillQuantityCreatorFragmentConfigProps = {
  title?: string;
  field: string;
  maxLength?: number;
  refillQuantityField?: string;
};

type RefillQuantityCreatorFragmentProps = {
  configProps: RefillQuantityCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function RefillQuantityCreatorFragment({
  configProps,
  commonProps,
  ref,
}: RefillQuantityCreatorFragmentProps) {
  const { task, onTaskChange, showValidation, taskType } = commonProps;

  const itemNoAndType = validateInitialDetailsFieldValue<ItemNoAndType>(
    getByPath(task, "details.item"),
    "details.item",
    taskType
  );

  const refillLocation = validateInitialDetailsFieldValue<Location>(
    getByPath(task, "details.refill_location"),
    "details.refill_location",
    taskType
  );

  const { filteredLocations } = useFilteredSalesLocations(itemNoAndType, "");

  const {
    activeUnit,
    handleRefillUnitTypeChange,
    panelConfigs,
    valid,
    currentPieces,
  } = useRefillQuantity({
    configProps,
    task: task as Record<string, unknown>,
    onTaskChange,
    itemNoAndType: itemNoAndType as ItemNoAndType | null,
    refillLocation: refillLocation as Location | null,
    filteredLocations,
  });

  const showEmptyQuantityValidation = showValidation && currentPieces <= 0;

  useImperativeHandle(ref, () => ({ valid }), [valid]);

  if (
    isLoading(task) ||
    isLoading(taskType) ||
    isLoading(itemNoAndType) ||
    isLoading(refillLocation)
  ) {
    return <LoadingCreatorFragment />;
  }

  return (
    <RefillQuantity
      activeUnit={activeUnit}
      onUnitChange={handleRefillUnitTypeChange}
      showValidation={showEmptyQuantityValidation}
      panelConfigs={panelConfigs}
    />
  );
}

export default RefillQuantityCreatorFragment;
